import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import AsyncCreatableSelect from "react-select/async-creatable";
import Selecto from "react-selecto";
import Select from "react-select";
import { Row, Col, Alert, Card, Button, OverlayTrigger, Tooltip } from "react-bootstrap";

import Dropzone from "../../layout/dropzone";
import Modal from "../../layout/modal";
import CsLineIcons from "../../cs-line-icons";

import Items from "./items";

import {
    list as listFiles,
    directories as listDirectories,
    createDirectory,
    download as downloadFile,
    downloadContents,
    downloadMany,
    deleteFile,
    deleteDirectory,
    eligibleShares,
    listShares,
    applyShares,
    renameFile,
    renameDirectory,
    moveFile,
    moveDirectory
} from "../../actions/files";
import {
    createEnvelope as createSignatureEnvelope
} from "../../actions/signatures";
import {
    send as sendFax
} from "../../actions/faxes";
import {
    search as searchPeople
} from "../../actions/people";
import {
    get as getMatters
} from "../../actions/matters";
import { deleteAgreement, listAgreements, updateAgreement } from "../../actions/agreement";


import Shares, { RolesCollection } from "../../layout/share";
import PersonSearch from "../../layout/select/person-search";
import { isGuid } from "../../utils/string";

const ACTIVITY_SUMMARY_COUNT = 2;
const VIRTUAL_AGREEMENTS_UUID = "virtual:agreements";
const VIRTUAL_AGREEMENT_DRAFTS_UUID = "virtual:agreement-drafts";
const VIRTUAL_AGREEMENT_FINALIZED_UUID = "virtual:agreement-finalized";
const VIRTUAL_MATTER_PREFIX = "virtual:matter:";

const FileBrowserToolbarButton = ({
    disabled = false,
    onClick,
    tooltip,
    children
}) => (
    <OverlayTrigger
        delay={{ show: 250, hide: 0 }}
        overlay={<Tooltip>{tooltip}</Tooltip>}
        placement="bottom"
    >
        <span className="file-browser-toolbar-button">
            <Button
                type="button"
                variant="outline-primary"
                className="btn-icon btn-icon-only"
                disabled={disabled}
                onClick={onClick}
                aria-label={tooltip}
            >
                {children}
            </Button>
        </span>
    </OverlayTrigger>
);

const CreateFolderIcon = () => (
    <span className="file-browser-create-folder-icon" aria-hidden="true">
        <CsLineIcons icon="folder" />
        <CsLineIcons icon="plus" className="file-browser-create-folder-icon-plus" />
    </span>
);

const createFaxRecipient = () => ({
    key: `recipient-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    personUuid: "",
    name: "",
    fax: ""
});

const createFaxState = ({ show = false, coverSheetPersonUuid = "" } = {}) => ({
    show,
    saving: false,
    recipients: [createFaxRecipient()],
    createCoverSheet: false,
    coverSheetSource: "manual",
    coverSheetPersonUuid,
    fromName: "",
    fromFax: "",
    subject: "",
    message: ""
});

const personDisplayName = (person = {}) => {
    const item = person || {};
    return item.displayName ||
        [item.firstName, item.middleName, item.lastName, item.generation].filter(Boolean).join(" ");
};

const faxRecipientNameValue = (recipient = {}) => {
    const label = String(recipient.name || "").trim();
    if (!label) return null;

    return {
        value: recipient.personUuid || label,
        label,
        data: isGuid(recipient.personUuid) ? { uuid: recipient.personUuid, displayName: label, fax: recipient.fax } : null
    };
};

const objectValue = (value) => {
    if (!value) return {};
    if (typeof value === "object" && !Array.isArray(value)) return value;
    if (typeof value === "string") {
        try {
            const parsed = JSON.parse(value);
            return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
        } catch {
            return {};
        }
    }
    return {};
};

const arrayValue = (value) => Array.isArray(value) ? value : [];

class Files extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            loading: false,
            error: null,

            directoryUuid: null,
            directory: null,
            virtualPath: null,
            virtualMatter: null,
            directories: [],
            allDirectories: [],
            breadcrumbs: [
                {
                    uuid: null,
                    name: "Home",
                    path: "\\"
                }
            ],

            items: [],
            selected: [],
            eligible: [],
            shareSearching: false,
            shareSearchText: "",

            contextMenu: {
                show: false,
                x: 0,
                y: 0
            },

            modal: {
                folder: {
                    show: false,
                    name: ""
                },
                destroy: {
                    show: false
                },
                error: {
                    show: false,
                    title: "",
                    body: ""
                },
                share: {
                    show: false,
                    shares: []
                },
                rename: {
                    show: false,
                    uuid: null,
                    type: null,
                    name: ""
                },
                move: {
                    show: false,
                    directoryUuid: ""
                },
                properties: {
                    show: false,
                    item: null
                },
                sign: {
                    show: false,
                    saving: false,
                    file: null,
                    subject: "",
                    messageBody: "",
                    envelopeUuid: null
                },
                fax: createFaxState()
            }
        };
    }

    componentDidMount = async () => {
        document.addEventListener("click", this.hideContextMenu);
        document.addEventListener("scroll", this.hideContextMenu, true);

        await this.fetchData();
    };

    componentWillUnmount = () => {
        document.removeEventListener("click", this.hideContextMenu);
        document.removeEventListener("scroll", this.hideContextMenu, true);
    };

    componentDidUpdate = async (prevProps) => {
        const changed =
            prevProps.ownerType !== this.props.ownerType ||
            prevProps.ownerUuid !== this.props.ownerUuid ||
            prevProps.contextType !== this.props.contextType ||
            prevProps.contextUuid !== this.props.contextUuid;

        if (changed) {
            this.setState(
                {
                    directoryUuid: null,
                    directory: null,
                    virtualPath: null,
                    virtualMatter: null,
                    selected: []
                },
                this.fetchData
            );
        }
    };

    buildUploadUrl = () => {
        const {
            ownerType,
            ownerUuid,
            contextType,
            contextUuid
        } = this.props;

        let url = `/files/o/${ownerType}/${ownerUuid}`;

        if (contextType && contextUuid) {
            url += `/c/${contextType}/${contextUuid}`;
        }

        url += "/upload";

        if (this.state.directoryUuid) {
            url += `?directoryUuid=${this.state.directoryUuid}`;
        }

        return url;
    };

    shareSearchSeq = 0;

    loadShareEligible = async (q = "") => {
        const seq = ++this.shareSearchSeq;

        this.setState({
            shareSearching: true,
            shareSearchText: q
        });

        try {
            const response = await this.props.eligibleShares({
                ...this.scope,
                q
            });

            if (seq !== this.shareSearchSeq) {
                return response.items || [];
            }

            this.setState({
                eligible: response.items || [],
                shareSearching: false
            });

            return response.items || [];
        } catch (error) {
            if (seq === this.shareSearchSeq) {
                this.setState({
                    eligible: [],
                    shareSearching: false
                });
            }

            return [];
        }
    }

    formatBytes = (bytes) => {
        const value = Number(bytes || 0);

        if (!value) {
            return "0 B";
        }

        const units = ["B", "KB", "MB", "GB", "TB"];
        let size = value;
        let unitIndex = 0;

        while (size >= 1024 && unitIndex < units.length - 1) {
            size = size / 1024;
            unitIndex += 1;
        }

        const decimals = size >= 10 || unitIndex === 0 ? 0 : 1;

        return `${size.toFixed(decimals)} ${units[unitIndex]}`;
    };

    formatDisplayDate = (dateValue) => {
        if (!dateValue) {
            return "";
        }

        const date = new Date(dateValue);

        if (isNaN(date.getTime())) {
            return "";
        }

        return date.toLocaleDateString("en-US", {
            month: "numeric",
            day: "numeric",
            year: "numeric"
        });
    };

    fileActivity = (item) => {
        const meta = objectValue(item?.meta);
        return arrayValue(meta.activity)
            .map((activity) => objectValue(activity))
            .filter((activity) => activity.category === "fax" || activity.category === "signature")
            .sort((a, b) => {
                const left = new Date(a.occurredAt || 0).getTime();
                const right = new Date(b.occurredAt || 0).getTime();
                return (isNaN(right) ? 0 : right) - (isNaN(left) ? 0 : left);
            });
    };

    activityLine = (activity) => {
        const date = this.formatDisplayDate(activity.occurredAt);
        return [date, activity.summary || activity.detail || activity.type]
            .filter(Boolean)
            .join(" ");
    };

    filePropertiesDetails = (item) => {
        if (item?.type === "agreement-draft") {
            return [
                `Status: ${item.status || "draft"}`,
                item.agreement?.draftLabel ? `Draft: ${item.agreement.draftLabel}` : null,
                item.agreement?.jurisdiction ? `Jurisdiction: ${item.agreement.jurisdiction}` : null
            ].filter(Boolean).join("\n");
        }

        const activity = this.fileActivity(item);
        if (activity.length === 0) {
            return "No fax or signature activity has been recorded for this file.";
        }

        return activity
            .map((event) => [
                this.activityLine(event),
                event.detail || ""
            ].filter(Boolean).join("\n"))
            .join("\n\n");
    };

    getRecursiveItemCount = (item) => {
        if (!item) {
            return 0;
        }

        if (item.type === "file") {
            return 1;
        }

        /*
         * Preferred backend field:
         * - item.itemCount
         *
         * Other tolerated names:
         * - item.count
         * - item.recursiveCount
         * - item.childCount
         *
         * This assumes the count is descendant contents only,
         * so the selected folder itself counts as 1.
         */
        const recursiveCount =
            item.itemCount ??
            item.count ??
            item.recursiveCount ??
            item.childCount ??
            0;

        return 1 + Number(recursiveCount || 0);
    };

    safeZipBaseName = (value) => {
        return String(value || "Home")
            .replace(/[<>:"/\\|?*]/g, "")
            .split("")
            .filter((character) => character.charCodeAt(0) >= 32)
            .join("")
            .replace(/\s+/g, " ")
            .trim() || "Home";
    };

    getCurrentFolderDownloadName = () => {
        const breadcrumbs = this.state.breadcrumbs || [];
        const folderName = this.state.directory?.name ||
            breadcrumbs[breadcrumbs.length - 1]?.name ||
            "Home";

        return `${this.safeZipBaseName(folderName)}-contents.zip`;
    };

    getSelectedSummary = () => {
        const selected = this.selectedItems;

        if (selected.length === 0) {
            return {
                count: 0,
                size: 0,
                name: "None selected",
                date: ""
            };
        }

        const count = selected.reduce((total, item) => {
            return total + this.getRecursiveItemCount(item);
        }, 0);

        const size = selected.reduce((total, item) => {
            return total + Number(item.size || 0);
        }, 0);

        const name = selected.length === 1
            ? selected[0].name || "Untitled"
            : "Multiple Selected";

        const latestDate = selected.reduce((latest, item) => {
            const dateValue = item.type === "directory"
                ? item.earliestFileDate || item.oldestFileDate || item.firstFileDate
                : item.updatedAt || item.createdAt;

            if (!dateValue) {
                return latest;
            }

            const itemDate = new Date(dateValue);

            if (isNaN(itemDate.getTime())) {
                return latest;
            }

            if (!latest || itemDate > latest) {
                return itemDate;
            }

            return latest;
        }, null);

        return {
            count,
            size,
            name,
            date: this.formatDisplayDate(latestDate)
        };
    };

    onItemContextMenu = ({ event, item }) => {
        const isAlreadySelected = this.state.selected.includes(item.uuid);

        this.setState({
            selected: isAlreadySelected
                ? this.state.selected
                : [item.uuid],
            contextMenu: {
                show: true,
                x: event.clientX,
                y: event.clientY
            }
        });
    };

    hideContextMenu = () => {
        if (!this.state.contextMenu.show) {
            return;
        }

        this.setState({
            contextMenu: {
                show: false,
                x: 0,
                y: 0
            }
        });
    };

    onContextMenuAction = async (action) => {
        this.hideContextMenu();

        if (action?.onClick) {
            await action.onClick();
        }
    };

    get scope() {
        if (this.state.virtualMatter?.uuid) {
            return {
                ownerType: "person",
                ownerUuid: this.props.ownerUuid,
                contextType: "matter",
                contextUuid: this.state.virtualMatter.uuid
            };
        }

        return {
            ownerType: this.props.ownerType,
            ownerUuid: this.props.ownerUuid,
            contextType: this.props.contextType,
            contextUuid: this.props.contextUuid
        };
    }

    get selectedItems() {
        const itemsByUuid = new Map((this.state.items || []).map((item) => [item.uuid, item]));
        return (this.state.selected || [])
            .map((uuid) => itemsByUuid.get(uuid))
            .filter(Boolean);
    }

    get selectedFileUuids() {
        return this.selectedItems
            .filter((item) => item.type === "file")
            .map((item) => item.uuid);
    }

    get selectedDirectoryUuids() {
        return this.selectedItems
            .filter((item) => item.type === "directory")
            .map((item) => item.uuid);
    }

    get selectedSignableFiles() {
        const selected = this.selectedItems;

        if (selected.length === 0 || selected.some((item) => item.type !== "file")) {
            return [];
        }

        return selected.every((item) => this.isSignableFile(item)) ? selected : [];
    }

    get selectedFaxableFiles() {
        if (this.selectedDirectoryUuids.length > 0) {
            return [];
        }

        return this.selectedItems.filter((item) => item.type === "file");
    }

    get selectedPropertyFile() {
        if (this.state.selected.length !== 1) {
            return null;
        }

        const item = this.selectedItems[0];
        return ["file", "agreement-draft"].includes(item?.type) ? item : null;
    }

    isSignableFile = (item) => {
        const name = String(item?.name || item?.originalName || "").toLowerCase();
        const extension = String(item?.extension || "").replace(/^\./, "").toLowerCase();
        return extension === "pdf" || extension === "docx" || name.endsWith(".pdf") || name.endsWith(".docx");
    };

    get selectedShareRole() {
        return this.selectedDirectoryUuids.length > 0 ? "folder" : "file";
    }

    shareRoleForLevel = (level) => {
        const roles = RolesCollection[this.selectedShareRole].values;
        const numericLevel = Number(level || 0);
        const exact = roles.find((option) => option.value === numericLevel);

        if (exact) return exact;
        if ((numericLevel & 4) === 4) return roles[roles.length - 1];
        if ((numericLevel & 2) === 2) return roles.find((option) => option.value === 3) || roles[0];
        if ((numericLevel & 1) === 1) return roles[0];
        return null;
    };

    get selectedActions() {
        const virtualItem = this.selectedItems.find((item) => item.virtualKind);
        if (virtualItem) {
            const actions = [{ caption: "Open", onClick: () => this.onOpenItem(virtualItem) }];
            if (virtualItem.type === "agreement-draft") {
                actions.push(
                    { caption: "Rename", onClick: this.onRenameSelected },
                    { caption: "Delete", onClick: this.onDestroySelected },
                    { caption: "Properties", onClick: this.onPropertiesSelected }
                );
            }
            return actions;
        }
        const actions = [];
        if (this.state.selected.length === 1) {
            actions.push({ caption: "Open", onClick: () => this.onOpenItem(this.selectedItems[0]) });
        }
        actions.push(
            { caption: "Download", onClick: this.onDownloadSelected },
            { caption: "Share", onClick: this.onShareSelected }
        );

        if (this.selectedSignableFiles.length > 0) {
            actions.push({ caption: "Sign", onClick: this.onSignSelected });
        }

        actions.push({ caption: "Move", onClick: this.onMoveSelected });

        if (this.selectedFaxableFiles.length > 0) {
            actions.push({ caption: "Fax", onClick: this.onFaxSelected });
        }

        if (this.state.selected.length === 1) {
            actions.push({ caption: "Rename", onClick: this.onRenameSelected });
        }

        actions.push({ caption: "Delete", onClick: this.onDestroySelected });

        if (this.selectedPropertyFile) {
            actions.push({ caption: "Properties", onClick: this.onPropertiesSelected });
        }

        return actions;
    }

    get hasAgreementExplorer() {
        return (typeof this.props.loadAgreements === "function" && typeof this.props.onOpenAgreement === "function")
            || this.isVirtualMatter;
    }

    get hasMatterExplorer() {
        return this.props.ownerType === "person" && !this.props.contextType && typeof this.props.getMatters === "function";
    }

    get isVirtualMatter() {
        return Boolean(this.state.virtualMatter?.uuid);
    }

    matterVirtualDirectory = (matter) => this.virtualDirectory(
        `${VIRTUAL_MATTER_PREFIX}${matter.uuid}`,
        matter.caption || "Untitled Matter",
        "matter",
        { matterUuid: matter.uuid, matter }
    );

    agreementDirectories = (directories = []) => {
        const agreements = directories.find((item) => !item.parentUuid && String(item.name || "").toLowerCase() === "agreements");
        const finalized = agreements
            ? directories.find((item) => item.parentUuid === agreements.uuid && String(item.name || "").toLowerCase() === "finalized")
            : null;
        return { agreements, finalized };
    };

    virtualDirectory = (uuid, name, virtualKind, source = {}) => ({
        ...source,
        uuid,
        name,
        type: "directory",
        virtualKind,
        realDirectoryUuid: source?.uuid || null
    });

    virtualBreadcrumbs = (virtualPath) => {
        const home = { uuid: null, name: "Home", path: "\\", virtualPath: null };
        const agreements = { uuid: VIRTUAL_AGREEMENTS_UUID, name: "Agreements", path: "\\Agreements", virtualPath: "agreements" };
        const prefix = this.isVirtualMatter
            ? this.matterVirtualBreadcrumbs(this.state.virtualMatter)
            : [home];
        if (virtualPath === "drafts") {
            return [...prefix, agreements, { uuid: VIRTUAL_AGREEMENT_DRAFTS_UUID, name: "Drafts", path: "\\Agreements\\Drafts", virtualPath: "drafts" }];
        }
        if (virtualPath === "finalized") {
            return [...prefix, agreements, { uuid: VIRTUAL_AGREEMENT_FINALIZED_UUID, name: "Finalized", path: "\\Agreements\\Finalized", virtualPath: "finalized" }];
        }
        return virtualPath === "agreements" ? [...prefix, agreements] : prefix;
    };

    matterVirtualBreadcrumbs = (matter) => [
        { uuid: null, name: "Home", path: "\\", virtualPath: null },
        {
            uuid: `${VIRTUAL_MATTER_PREFIX}${matter.uuid}`,
            name: matter.caption || "Untitled Matter",
            path: `\\${matter.caption || "Untitled Matter"}`,
            virtualPath: `matter:${matter.uuid}`,
            matter
        }
    ];

    agreementDraftItems = async () => {
        const response = typeof this.props.loadAgreements === "function"
            ? await this.props.loadAgreements()
            : await listAgreements(this.state.virtualMatter?.uuid);
        return (response?.items || [])
            .filter((item) => ["draft", "review"].includes(item.status))
            .map((item) => ({
                uuid: `agreement-draft:${item.uuid}`,
                agreementUuid: item.uuid,
                agreement: item,
                name: item.title || "Untitled Agreement",
                type: "agreement-draft",
                virtualKind: "agreement-draft",
                status: item.status,
                size: 0,
                createdAt: item.createdAt,
                updatedAt: item.updatedAt
            }));
    };

    fetchData = async () => {
        this.setState({
            loading: true,
            error: null
        });

        try {
            const directoriesResponse = await this.props.listDirectories({ ...this.scope });
            const allDirectories = directoriesResponse.items || [];
            const agreementDirectories = this.agreementDirectories(allDirectories);
            const virtualPath = this.hasAgreementExplorer ? this.state.virtualPath : null;
            let response = null;
            let items = [];
            let directory = null;
            let directories = [];
            let breadcrumbs = null;

            if (virtualPath === "agreements") {
                items = [
                    this.virtualDirectory(VIRTUAL_AGREEMENT_DRAFTS_UUID, "Drafts", "agreement-drafts"),
                    this.virtualDirectory(VIRTUAL_AGREEMENT_FINALIZED_UUID, "Finalized", "agreement-finalized", agreementDirectories.finalized || {})
                ];
                directory = this.virtualDirectory(VIRTUAL_AGREEMENTS_UUID, "Agreements", "agreements", agreementDirectories.agreements || {});
                breadcrumbs = this.virtualBreadcrumbs("agreements");
            } else if (virtualPath === "drafts") {
                items = await this.agreementDraftItems();
                directory = this.virtualDirectory(VIRTUAL_AGREEMENT_DRAFTS_UUID, "Drafts", "agreement-drafts");
                breadcrumbs = this.virtualBreadcrumbs("drafts");
            } else if (virtualPath === "finalized") {
                if (agreementDirectories.finalized) {
                    response = await this.props.listFiles({ ...this.scope, directoryUuid: agreementDirectories.finalized.uuid });
                    items = response.items || [];
                    directories = response.directories || [];
                }
                directory = this.virtualDirectory(VIRTUAL_AGREEMENT_FINALIZED_UUID, "Finalized", "agreement-finalized", agreementDirectories.finalized || {});
                breadcrumbs = this.virtualBreadcrumbs("finalized");
            } else if (this.isVirtualMatter) {
                response = await this.props.listFiles({ ...this.scope, directoryUuid: this.state.directoryUuid });
                items = response.items || [];
                directories = response.directories || [];
                directory = response.directory || this.matterVirtualDirectory(this.state.virtualMatter);
                breadcrumbs = this.state.directoryUuid
                    ? [
                        ...this.matterVirtualBreadcrumbs(this.state.virtualMatter),
                        ...(response.breadcrumbs || []).slice(1)
                    ]
                    : this.matterVirtualBreadcrumbs(this.state.virtualMatter);
                if (!this.state.directoryUuid) {
                    items = [
                        this.virtualDirectory(VIRTUAL_AGREEMENTS_UUID, "Agreements", "agreements", agreementDirectories.agreements || {}),
                        ...items.filter((item) => item.uuid !== agreementDirectories.agreements?.uuid)
                    ];
                }
            } else {
                response = await this.props.listFiles({ ...this.scope, directoryUuid: this.state.directoryUuid });
                items = response.items || [];
                directories = response.directories || [];
                directory = response.directory || null;
                breadcrumbs = response.breadcrumbs || null;
                if (!this.state.directoryUuid && this.hasAgreementExplorer) {
                    items = [
                        this.virtualDirectory(VIRTUAL_AGREEMENTS_UUID, "Agreements", "agreements", agreementDirectories.agreements || {}),
                        ...items.filter((item) => item.uuid !== agreementDirectories.agreements?.uuid)
                    ];
                }
                if (!this.state.directoryUuid && this.hasMatterExplorer) {
                    const matterResponse = await this.props.getMatters({ personUuid: this.props.ownerUuid });
                    const matterFolders = (matterResponse?.items || [])
                        .filter((matter) => matter?.uuid)
                        .map(this.matterVirtualDirectory);
                    items = [...matterFolders, ...items];
                }
            }

            this.setState({
                loading: false,
                directory,
                directoryUuid: virtualPath === "finalized" ? agreementDirectories.finalized?.uuid || null : this.state.directoryUuid,
                directories,
                allDirectories,
                breadcrumbs: breadcrumbs || [
                    {
                        uuid: null,
                        name: "Home",
                        path: "\\"
                    }
                ],
                items,
                selected: []
            });
        } catch (error) {
            this.setState({
                loading: false,
                error: "Unable to load files."
            });
        }
    };

    onSelectItem = ({ item, additive = false }) => {
        if (!item?.uuid) {
            return;
        }

        if (!additive) {
            this.setState({
                selected: [item.uuid],
                contextMenu: {
                    show: false,
                    x: 0,
                    y: 0
                }
            });

            return;
        }

        const selected = [...this.state.selected];
        const index = selected.indexOf(item.uuid);

        if (index === -1) {
            selected.push(item.uuid);
        } else {
            selected.splice(index, 1);
        }

        this.setState({
            selected,
            contextMenu: {
                show: false,
                x: 0,
                y: 0
            }
        });
    };

    onOpenItem = async (item) => {
        if (item.type === "agreement-draft") {
            if (typeof this.props.onOpenAgreement === "function") {
                this.props.onOpenAgreement(item.agreementUuid, item);
            } else if (this.state.virtualMatter?.uuid) {
                window.location.href = `/dashboard/matters/${this.state.virtualMatter.uuid}?agreement=${item.agreementUuid}`;
            }
            return;
        }

        if (item.virtualKind) {
            if (item.virtualKind === "matter") {
                this.setState(
                    {
                        virtualPath: `matter:${item.matterUuid}`,
                        virtualMatter: item.matter,
                        directoryUuid: null,
                        directory: item,
                        selected: []
                    },
                    this.fetchData
                );
                return;
            }
            const virtualPath = item.virtualKind === "agreements"
                ? "agreements"
                : item.virtualKind === "agreement-drafts"
                    ? "drafts"
                    : "finalized";
            this.setState(
                {
                    virtualPath,
                    virtualMatter: this.state.virtualMatter,
                    directoryUuid: virtualPath === "finalized" ? item.realDirectoryUuid || null : null,
                    directory: item,
                    selected: []
                },
                this.fetchData
            );
            return;
        }

        if (item.type === "directory") {
            this.setState(
                {
                    virtualPath: null,
                    directoryUuid: item.uuid,
                    directory: item,
                    selected: []
                },
                this.fetchData
            );

            return;
        }

        await this.props.downloadFile({
            uuid: item.uuid,
            name: item.name
        });
    };

    onBreadcrumbClick = (crumb) => {
        const isVirtual = Object.prototype.hasOwnProperty.call(crumb || {}, "virtualPath");
        const isHome = isVirtual && !crumb?.virtualPath;
        const matter = crumb?.matter || (!isHome ? this.state.virtualMatter : null);
        this.setState(
            {
                virtualPath: isVirtual ? crumb.virtualPath : null,
                virtualMatter: matter,
                directoryUuid: isVirtual ? null : crumb.uuid || null,
                selected: []
            },
            this.fetchData
        );
    };

    onPathUp = () => {
        const breadcrumbs = this.state.breadcrumbs || [];

        if (breadcrumbs.length <= 1) {
            return;
        }

        const parent = breadcrumbs[breadcrumbs.length - 2];

        this.onBreadcrumbClick(parent);
    };

    onFolderCreate = () => {
        this.setState({
            modal: {
                ...this.state.modal,
                folder: {
                    show: true,
                    name: ""
                }
            }
        });
    };

    onFolderCancel = () => {
        this.setState({
            modal: {
                ...this.state.modal,
                folder: {
                    show: false,
                    name: ""
                }
            }
        });
    };

    onFolderNameChange = (event) => {
        this.setState({
            modal: {
                ...this.state.modal,
                folder: {
                    ...this.state.modal.folder,
                    name: event.target.value
                }
            }
        });
    };

    onFolderOk = async () => {
        const name = (this.state.modal.folder.name || "").trim();

        if (!name) {
            return;
        }

        await this.props.createDirectory({
            ...this.scope,
            parentUuid: this.state.directoryUuid,
            name
        });

        this.onFolderCancel();

        await this.fetchData();
    };

    onFileUpload = async (response) => {
        if (response?.success === false) {
            this.setState({
                error: response?.error?.message || "Upload failed."
            });

            return;
        }

        await this.fetchData();
    };

    onFilesUpload = async (responses) => {
        const failed = responses.find((response) => response?.success === false);

        if (failed) {
            this.setState({
                error: failed?.error?.message || "One or more uploads failed."
            });
            return;
        }

        await this.fetchData();
    };

    onDragSelect = (event) => {
        const selectedUuids = event.selected
            .map((element) => element.getAttribute("data-file-uuid"))
            .filter(Boolean);

        if (event.inputEvent?.ctrlKey || event.inputEvent?.metaKey) {
            const merged = Array.from(new Set([
                ...this.state.selected,
                ...selectedUuids
            ]));

            this.setState({
                selected: merged
            });

            return;
        }

        this.setState({
            selected: selectedUuids
        });
    };

    onDownloadSelected = async () => {
        if (this.state.loading) {
            return;
        }

        if (this.state.selected.length === 0) {
            await this.onDownloadContents();
            return;
        }

        if (this.state.selected.length === 1 && this.selectedFileUuids.length === 1) {
            const file = this.selectedItems[0];

            await this.props.downloadFile({
                uuid: file.uuid,
                name: file.name
            });

            return;
        }

        await this.props.downloadMany({
            files: this.selectedFileUuids,
            directories: this.selectedDirectoryUuids,
            name: "files.zip"
        });
    };

    onDownloadContents = async () => {
        if (this.state.loading) {
            return;
        }

        try {
            await this.props.downloadContents({
                ...this.scope,
                directoryUuid: this.state.directoryUuid,
                name: this.getCurrentFolderDownloadName()
            });
        } catch (error) {
            this.setState({
                error: error?.response?.data?.error?.message ||
                    error?.message ||
                    "Unable to download folder contents."
            });
        }
    };

    onRenameSelected = () => {
        if (this.state.selected.length !== 1) {
            return;
        }

        const item = this.selectedItems[0];

        if (!item) {
            return;
        }

        this.setState({
            modal: {
                ...this.state.modal,
                rename: {
                    show: true,
                    uuid: item.type === "agreement-draft" ? item.agreementUuid : item.uuid,
                    type: item.type,
                    name: item.name || ""
                }
            }
        });
    };

    onRenameCancel = () => {
        this.setState({
            modal: {
                ...this.state.modal,
                rename: {
                    show: false,
                    uuid: null,
                    type: null,
                    name: ""
                }
            }
        });
    };

    onRenameNameChange = (event) => {
        this.setState({
            modal: {
                ...this.state.modal,
                rename: {
                    ...this.state.modal.rename,
                    name: event.target.value
                }
            }
        });
    };

    onRenameOk = async () => {
        const { uuid, type, name } = this.state.modal.rename;
        const cleanName = String(name || "").trim();

        if (!uuid || !type || !cleanName) {
            return;
        }

        this.onRenameCancel();

        if (type === "agreement-draft") {
            await updateAgreement(uuid, { title: cleanName });
        } else if (type === "directory") {
            await this.props.renameDirectory({
                uuid,
                name: cleanName
            });
        } else {
            await this.props.renameFile({
                uuid,
                name: cleanName
            });
        }

        await this.fetchData();
    };

    onMoveSelected = () => {
        if (this.state.selected.length === 0) {
            return;
        }

        this.setState({
            modal: {
                ...this.state.modal,
                move: {
                    show: true,
                    directoryUuid: ""
                }
            }
        });
    };

    onMoveCancel = () => {
        this.setState({
            modal: {
                ...this.state.modal,
                move: {
                    show: false,
                    directoryUuid: ""
                }
            }
        });
    };

    onMoveDirectoryChange = (option) => {
        this.setState({
            modal: {
                ...this.state.modal,
                move: {
                    ...this.state.modal.move,
                    directoryUuid: option?.value || ""
                }
            }
        });
    };

    onMoveOk = async () => {
        const targetDirectoryUuid = this.state.modal.move.directoryUuid || null;

        this.onMoveCancel();

        for (const item of this.selectedItems) {
            if (item.type === "directory") {
                await this.props.moveDirectory({
                    uuid: item.uuid,
                    directoryUuid: targetDirectoryUuid
                });
            } else {
                await this.props.moveFile({
                    uuid: item.uuid,
                    directoryUuid: targetDirectoryUuid
                });
            }
        }

        await this.fetchData();
    };

    onPropertiesSelected = () => {
        const item = this.selectedPropertyFile;
        if (!item) {
            return;
        }

        this.setState({
            modal: {
                ...this.state.modal,
                properties: {
                    show: true,
                    item
                }
            }
        });
    };

    onPropertiesCancel = () => {
        this.setState({
            modal: {
                ...this.state.modal,
                properties: {
                    show: false,
                    item: null
                }
            }
        });
    };

    onSignSelected = async () => {
        const files = this.selectedSignableFiles;
        if (files.length === 0) {
            return;
        }

        const file = files[0];
        const fileNames = files.map((item) => item.name || item.originalName || "Document");
        const baseName = files.length === 1
            ? String(file.name || "Document").replace(/\.[^.]+$/, "")
            : `${files.length} documents`;
        this.setState({ loading: true });

        const response = await this.props.createSignatureEnvelope({
            ...this.scope,
            matterUuid: this.props.contextType === "matter" ? this.props.contextUuid : null,
            personUuid: this.props.ownerType === "person" ? this.props.ownerUuid : null,
            ownerPersonUuid: this.props.ownerType === "person" ? this.props.ownerUuid : null,
            sourceFileUuid: file.uuid,
            originalFileName: file.name,
            title: files.length === 1 ? file.name : `Signature request: ${files.length} documents`,
            displayName: files.length === 1 ? file.name : `${files.length} documents`,
            originalFileOwnerType: this.props.ownerType || null,
            originalFileOwnerUuid: this.props.ownerUuid || null,
            documentOwnerType: this.props.ownerType || null,
            documentOwnerUuid: this.props.ownerUuid || null,
            directoryUuid: this.state.directoryUuid || null,
            creationSource: "document_selection",
            sourceApplication: "matters.firm.files",
            subject: `Signature request: ${baseName}`,
            messageBody: "Please review and sign this document.",
            documents: files.map((item, index) => ({
                sourceFileUuid: item.uuid,
                originalFileName: item.name,
                fileName: item.name,
                displayName: item.name,
                documentOrder: index,
                directoryUuid: this.state.directoryUuid || null
            })),
            metadata: {
                source: "matters.firm.files",
                sourceFileName: file.name,
                sourceFileNames: fileNames,
                sourceFileUuids: files.map((item) => item.uuid)
            }
        });

        this.setState({ loading: false });

        if (!response?.success) {
            this.setState({
                modal: {
                    ...this.state.modal,
                    error: {
                        show: true,
                        title: "Signature Request",
                        body: response?.error?.message || response?.error || "Unable to create the signature request."
                    }
                }
            });
            return;
        }

        const envelopeUuid = response.item?.uuid || response.uuid || null;
        if (envelopeUuid) {
            window.location.href = `/dashboard/signatures/${envelopeUuid}`;
        }
    };

    defaultFaxPersonUuid = () => {
        return this.props.ownerType === "person" ? this.props.ownerUuid : "";
    };

    currentFaxSourcePath = () => {
        const names = (this.state.breadcrumbs || [])
            .map((item) => String(item?.name || "").trim())
            .filter(Boolean);

        return names.length ? names.join("/") : "Home";
    };

    get selectedFaxRecipients() {
        return (this.state.modal.fax.recipients || [])
            .map((recipient) => ({
                personUuid: isGuid(recipient.personUuid) ? recipient.personUuid : null,
                name: String(recipient.name || "").trim(),
                fax: String(recipient.fax || "").trim()
            }))
            .filter((recipient) => recipient.fax);
    }

    get hasFaxRecipients() {
        return this.selectedFaxRecipients.length > 0;
    }

    onFaxSelected = () => {
        const files = this.selectedFaxableFiles;
        if (files.length === 0) {
            return;
        }

        this.setState({
            modal: {
                ...this.state.modal,
                fax: createFaxState({
                    show: true,
                    coverSheetPersonUuid: this.defaultFaxPersonUuid()
                })
            }
        });
    };

    onFaxCancel = () => {
        this.setState({
            modal: {
                ...this.state.modal,
                fax: createFaxState()
            }
        });
    };

    onFaxFieldChange = (event) => {
        const { name, value, checked, type } = event.target;
        this.setState({
            modal: {
                ...this.state.modal,
                fax: {
                    ...this.state.modal.fax,
                    [name]: type === "checkbox" ? checked : value
                }
            }
        });
    };

    onFaxRecipientFieldChange = (index, event) => {
        const { name, value } = event.target;
        this.setState((prev) => {
            const recipients = [...(prev.modal.fax.recipients || [])];
            recipients[index] = {
                ...recipients[index],
                [name]: value
            };

            return {
                modal: {
                    ...prev.modal,
                    fax: {
                        ...prev.modal.fax,
                        recipients
                    }
                }
            };
        });
    };

    loadFaxRecipientOptions = async (inputValue) => {
        const q = String(inputValue || "").trim();
        if (q.length < 2) return [];

        try {
            const response = await this.props.searchPeople({
                search: q,
                size: 100
            });

            return (response?.items || [])
                .map((person) => {
                    const label = personDisplayName(person);
                    if (!label) return null;
                    return {
                        value: person.uuid,
                        label,
                        data: person
                    };
                })
                .filter(Boolean);
        } catch {
            return [];
        }
    };

    onFaxRecipientNameChange = (index, option) => {
        this.setState((prev) => {
            const recipients = [...(prev.modal.fax.recipients || [])];
            const current = recipients[index] || createFaxRecipient();
            const item = option?.data || null;
            const selectedPersonUuid = isGuid(item?.uuid || option?.value) && item
                ? item.uuid || option.value
                : "";
            const selectedName = item
                ? personDisplayName(item)
                : String(option?.label || option?.value || "").trim();

            recipients[index] = {
                ...current,
                personUuid: selectedPersonUuid,
                name: selectedName,
                fax: item?.fax || current.fax
            };

            if (!option) {
                recipients[index] = {
                    ...current,
                    personUuid: "",
                    name: ""
                };
            }

            return {
                modal: {
                    ...prev.modal,
                    fax: {
                        ...prev.modal.fax,
                        recipients
                    }
                }
            };
        });
    };

    onFaxAddRecipient = () => {
        this.setState((prev) => ({
            modal: {
                ...prev.modal,
                fax: {
                    ...prev.modal.fax,
                    recipients: [
                        ...(prev.modal.fax.recipients || []),
                        createFaxRecipient()
                    ]
                }
            }
        }));
    };

    onFaxRemoveRecipient = (index) => {
        this.setState((prev) => {
            const recipients = (prev.modal.fax.recipients || []).filter((_, itemIndex) => itemIndex !== index);
            return {
                modal: {
                    ...prev.modal,
                    fax: {
                        ...prev.modal.fax,
                        recipients: recipients.length ? recipients : [createFaxRecipient()]
                    }
                }
            };
        });
    };

    onFaxCoverSheetPersonChange = (event) => {
        const item = event?.target?.item?.data || null;
        this.setState({
            modal: {
                ...this.state.modal,
                fax: {
                    ...this.state.modal.fax,
                    coverSheetPersonUuid: event?.target?.value || "",
                    fromName: personDisplayName(item) || this.state.modal.fax.fromName,
                    fromFax: item?.fax || this.state.modal.fax.fromFax
                }
            }
        });
    };

    onFaxOk = async () => {
        const fax = this.state.modal.fax;
        const files = this.selectedFaxableFiles;
        if (files.length === 0 || fax.saving) return;
        const recipients = this.selectedFaxRecipients;

        if (recipients.length < 1) {
            this.setState({
                modal: {
                    ...this.state.modal,
                    error: {
                        show: true,
                        title: "Fax",
                        body: "Add at least one recipient fax number."
                    }
                }
            });
            return;
        }

        this.setState({
            modal: {
                ...this.state.modal,
                fax: {
                    ...fax,
                    saving: true
                }
            }
        });

        try {
            const sourcePath = this.currentFaxSourcePath();
            const response = await this.props.sendFax({
                ...this.scope,
                directoryUuid: this.state.directoryUuid || null,
                files: files.map((file) => file.uuid),
                recipients,
                recipientName: recipients[0]?.name || "",
                recipientFax: recipients[0]?.fax || "",
                recipientPersonUuid: recipients[0]?.personUuid || null,
                subject: fax.subject,
                message: fax.message,
                personUuid: this.props.ownerType === "person" ? this.props.ownerUuid : null,
                matterUuid: this.props.contextType === "matter" ? this.props.contextUuid : null,
                coverSheet: {
                    enabled: fax.createCoverSheet,
                    source: fax.coverSheetSource,
                    personUuid: fax.coverSheetPersonUuid || null,
                    data: {
                        fromName: fax.fromName,
                        fromFax: fax.fromFax,
                        subject: fax.subject,
                        message: fax.message
                    }
                },
                metadata: {
                    source: "matters.firm.files",
                    sourcePath,
                    sourceFileNames: files.map((file) => file.name),
                    sourceFiles: files.map((file) => ({
                        uuid: file.uuid,
                        name: file.name,
                        path: `${sourcePath}/${file.name}`
                    }))
                }
            });

            if (!response?.success) {
                throw new Error(response?.error?.message || response?.error || "Unable to send fax.");
            }

            this.onFaxCancel();
            await this.fetchData();
        } catch (error) {
            this.setState({
                modal: {
                    ...this.state.modal,
                    fax: {
                        ...this.state.modal.fax,
                        saving: false
                    },
                    error: {
                        show: true,
                        title: "Fax",
                        body: error?.response?.data?.error?.message ||
                            error?.message ||
                            "Unable to send fax."
                    }
                }
            });
        }
    };

    onDestroySelected = async () => {
        if (this.state.selected.length === 0) {
            return;
        }

        const containsDirectory = this.selectedItems.some((item) => {
            return item.type === "directory";
        });
        const containsAgreement = this.selectedItems.some((item) => item.type === "agreement-draft");

        this.setState({
            modal: {
                ...this.state.modal,
                destroy: {
                    show: true,
                    title: "Confirm deletion",
                    body: containsAgreement
                        ? "Confirm deletion of the selected agreement. This permanently removes the draft and its version history."
                        : containsDirectory
                        ? "Confirm deletion of the selected files and folders. Folders must be empty before they can be deleted."
                        : "Confirm deletion of the selected files. This will permanently remove them from storage."
                }
            }
        });
    };

    onDestroyCancel = () => {
        this.setState({
            modal: {
                ...this.state.modal,
                destroy: {
                    show: false
                }
            }
        });
    };

    onDestroyOk = async () => {
        this.onDestroyCancel();

        try {
            for (const item of this.selectedItems) {
                if (item.type === "agreement-draft") {
                    await deleteAgreement(item.agreementUuid);
                } else if (item.type === "directory") {
                    await this.props.deleteDirectory({
                        uuid: item.uuid
                    });
                } else {
                    await this.props.deleteFile({
                        uuid: item.uuid
                    });
                }
            }

            await this.fetchData();
        } catch (error) {
            this.setState({
                modal: {
                    ...this.state.modal,
                    error: {
                        show: true,
                        title: "Error",
                        body: error?.response?.data?.error?.message ||
                            error?.message ||
                            "Unable to delete one or more selected items."
                    }
                }
            });
        }
    };

    onErrorOk = () => {
        this.setState({
            modal: {
                ...this.state.modal,
                error: {
                    show: false,
                    title: "",
                    body: ""
                }
            }
        });
    };

    onShareSelected = async () => {
        const selected = this.selectedItems;

        if (selected.length === 0) return;

        const eligible = await this.loadShareEligible("");

        const shareMap = new Map();

        for (const item of selected) {
            const response = await this.props.listShares({
                fileUuid: item.type === "file" ? item.uuid : null,
                directoryUuid: item.type === "directory" ? item.uuid : null
            });

            const shares = response.items || [];

            shares.forEach((share) => {
                const person = share.person || eligible.find((item) => item.uuid === share.personUuid);
                if (!person) return;

                const existing = shareMap.get(share.personUuid);
                const level = Number(share.flags || 0);

                if (!existing || level > existing.level) {
                    const role = this.shareRoleForLevel(level);

                    shareMap.set(share.personUuid, {
                        uuid: share.personUuid,
                        name:
                            person.displayName ||
                            [person.firstName, person.lastName].filter(Boolean).join(" "),
                        level,
                        tags: role?.tags || []
                    });
                }
            });
        }

        this.setState({
            modal: {
                ...this.state.modal,
                share: {
                    show: true,
                    shares: Array.from(shareMap.values())
                }
            }
        });
    };

    onShareCancel = () => {
        this.setState({
            modal: {
                ...this.state.modal,
                share: {
                    show: false,
                    shares: []
                }
            }
        });
    };

    onShareOk = async (shares) => {
        this.onShareCancel();

        await this.props.applyShares({
            files: this.selectedFileUuids,
            directories: this.selectedDirectoryUuids,
            shares: shares.map((share) => ({
                personUuid: share.uuid,
                flags: share.level
            }))
        });

        await this.fetchData();
    };

    renderBreadcrumbs = () => {
        const breadcrumbs = this.state.breadcrumbs || [];

        return (
            <div className="d-flex flex-wrap align-items-center gap-1">
                {breadcrumbs.map((crumb, index) => (
                    <React.Fragment key={`breadcrumb_${index}`}>
                        {index > 0 ? (
                            <span className="text-muted">/</span>
                        ) : null}

                        <button
                            type="button"
                            className="btn btn-link p-0"
                            onClick={() => this.onBreadcrumbClick(crumb)}
                        >
                            {crumb.name}
                        </button>
                    </React.Fragment>
                ))}
            </div>
        );
    };

    renderFolderModal = () => {
        return (
            <Modal
                show={this.state.modal.folder.show}
                title="Create Folder"
                buttons={[
                    {
                        default: true,
                        caption: "Cancel",
                        onClick: this.onFolderCancel,
                        variant: "secondary",
                        display: true,
                        enabled: true
                    },
                    {
                        caption: "Create",
                        onClick: this.onFolderOk,
                        variant: "outline-primary",
                        display: true,
                        enabled: true
                    }
                ]}
            >
                <div className="mb-3 top-label">
                    <input
                        className="form-control"
                        value={this.state.modal.folder.name}
                        onChange={this.onFolderNameChange}
                        autoFocus
                    />
                    <label>FOLDER NAME</label>
                </div>
            </Modal>
        );
    };

    renderRenameModal = () => {
        return (
            <Modal
                show={this.state.modal.rename.show}
                title="Rename"
                buttons={[
                    {
                        default: true,
                        caption: "cancel",
                        onClick: this.onRenameCancel,
                        variant: "secondary"
                    },
                    {
                        caption: "ok",
                        onClick: this.onRenameOk,
                        variant: "outline-primary"
                    }
                ]}
            >
                <div className="mb-3 top-label">
                    <input
                        className="form-control"
                        value={this.state.modal.rename.name}
                        onChange={this.onRenameNameChange}
                    />
                    <label>NAME</label>
                </div>
            </Modal>
        );
    };

    renderMoveModal = () => {
        const selectedDirectoryUuids = new Set(this.selectedDirectoryUuids);

        const availableDirectories = (this.state.allDirectories || []).filter((directory) => {
            return !selectedDirectoryUuids.has(directory.uuid);
        });
        const directoryOptions = [
            { value: "", label: "Home" },
            ...availableDirectories.map((directory) => ({
                value: directory.uuid,
                label: directory.path,
            })),
        ];
        const selectedDirectoryOption = directoryOptions.find((option) => option.value === this.state.modal.move.directoryUuid) || directoryOptions[0];

        return (
            <Modal
                show={this.state.modal.move.show}
                title="Move"
                buttons={[
                    {
                        default: true,
                        caption: "cancel",
                        onClick: this.onMoveCancel,
                        variant: "secondary"
                    },
                    {
                        caption: "ok",
                        onClick: this.onMoveOk,
                        variant: "outline-primary"
                    }
                ]}
            >
                <div className="mb-3 top-label">
                    <Select
                        inputId="moveDirectoryUuid"
                        classNamePrefix="react-select"
                        value={selectedDirectoryOption}
                        onChange={this.onMoveDirectoryChange}
                        isClearable={false}
                        options={directoryOptions}
                    />
                    <label>DESTINATION FOLDER</label>
                </div>
            </Modal>
        );
    };

    renderFaxModal = () => {
        const fax = this.state.modal.fax;

        return (
            <Modal
                show={fax.show}
                title="Fax"
                size="lg"
                buttons={[
                    {
                        default: true,
                        caption: "Cancel",
                        onClick: this.onFaxCancel,
                        variant: "secondary",
                        display: true,
                        enabled: !fax.saving
                    },
                    {
                        caption: fax.saving ? "Sending..." : "Send Fax",
                        onClick: this.onFaxOk,
                        variant: "outline-primary",
                        display: true,
                        enabled: !fax.saving && this.selectedFaxableFiles.length > 0 && this.hasFaxRecipients
                    }
                ]}
            >
                <Row className="g-3">
                    <Col xs="12">
                        <div className="d-flex align-items-center justify-content-between">
                            <div className="fw-semibold">Recipients</div>
                            <Button
                                type="button"
                                size="sm"
                                variant="outline-primary"
                                disabled={fax.saving}
                                onClick={this.onFaxAddRecipient}
                            >
                                Add Recipient
                            </Button>
                        </div>
                    </Col>

                    {(fax.recipients || []).map((recipient, index) => (
                        <Col xs="12" key={recipient.key || index}>
                            <Row className="g-2 align-items-end">
                                <Col lg="7">
                                    <div className="top-label">
                                        <AsyncCreatableSelect
                                            inputId={`faxRecipientName${index}`}
                                            name="name"
                                            classNamePrefix="react-select"
                                            cacheOptions
                                            defaultOptions={false}
                                            isClearable
                                            loadOptions={this.loadFaxRecipientOptions}
                                            value={faxRecipientNameValue(recipient)}
                                            onChange={(option) => this.onFaxRecipientNameChange(index, option)}
                                            onCreateOption={(inputValue) => this.onFaxRecipientNameChange(index, {
                                                value: inputValue,
                                                label: inputValue
                                            })}
                                            autoFocus={index === 0}
                                            formatCreateLabel={(inputValue) => inputValue}
                                        />
                                        <label>RECIPIENT NAME</label>
                                    </div>
                                </Col>

                                <Col lg="3">
                                    <div className="top-label">
                                        <input
                                            className="form-control"
                                            name="fax"
                                            value={recipient.fax}
                                            onChange={(event) => this.onFaxRecipientFieldChange(index, event)}
                                        />
                                        <label>RECIPIENT FAX</label>
                                    </div>
                                </Col>

                                <Col lg="1" className="d-flex justify-content-end">
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="link"
                                        className="px-0"
                                        disabled={fax.saving || (fax.recipients || []).length <= 1}
                                        onClick={() => this.onFaxRemoveRecipient(index)}
                                    >
                                        Remove
                                    </Button>
                                </Col>
                            </Row>
                        </Col>
                    ))}

                    <Col xs="12">
                        <label className="form-check mb-0">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                name="createCoverSheet"
                                checked={fax.createCoverSheet}
                                onChange={this.onFaxFieldChange}
                            />
                            <span className="form-check-label">Create coversheet</span>
                        </label>
                    </Col>

                    {fax.createCoverSheet ? (
                        <>
                            <Col md="6">
                                <div className="top-label">
                                    <Select
                                        inputId="faxCoverSheetSource"
                                        name="coverSheetSource"
                                        classNamePrefix="react-select"
                                        value={[
                                            { value: "manual", label: "Manual" },
                                            { value: "person", label: "Person Record" }
                                        ].find((option) => option.value === fax.coverSheetSource)}
                                        onChange={(option) => this.onFaxFieldChange({
                                            target: {
                                                name: "coverSheetSource",
                                                value: option?.value || "manual"
                                            }
                                        })}
                                        options={[
                                            { value: "manual", label: "Manual" },
                                            { value: "person", label: "Person Record" }
                                        ]}
                                    />
                                    <label>COVERSHEET SOURCE</label>
                                </div>
                            </Col>

                            {fax.coverSheetSource === "person" ? (
                                <Col md="6">
                                    <div className="top-label">
                                        <PersonSearch
                                            name="coverSheetPersonUuid"
                                            value={fax.coverSheetPersonUuid}
                                            onChange={this.onFaxCoverSheetPersonChange}
                                        />
                                        <label>PERSON</label>
                                    </div>
                                </Col>
                            ) : null}

                            <Col md="6">
                                <div className="top-label">
                                    <input
                                        className="form-control"
                                        name="fromName"
                                        value={fax.fromName}
                                        onChange={this.onFaxFieldChange}
                                    />
                                    <label>FROM NAME</label>
                                </div>
                            </Col>

                            <Col md="6">
                                <div className="top-label">
                                    <input
                                        className="form-control"
                                        name="fromFax"
                                        value={fax.fromFax}
                                        onChange={this.onFaxFieldChange}
                                    />
                                    <label>FROM FAX</label>
                                </div>
                            </Col>

                            <Col xs="12">
                                <div className="top-label">
                                    <input
                                        className="form-control"
                                        name="subject"
                                        value={fax.subject}
                                        onChange={this.onFaxFieldChange}
                                    />
                                    <label>SUBJECT</label>
                                </div>
                            </Col>

                            <Col xs="12">
                                <div className="top-label">
                                    <textarea
                                        className="form-control"
                                        name="message"
                                        rows={5}
                                        value={fax.message}
                                        onChange={this.onFaxFieldChange}
                                    />
                                    <label>MESSAGE</label>
                                </div>
                            </Col>
                        </>
                    ) : null}
                </Row>
            </Modal>
        );
    };

    renderRecentActivity = (item) => {
        if (!item) {
            return null;
        }

        const activity = this.fileActivity(item).slice(0, ACTIVITY_SUMMARY_COUNT);
        if (activity.length === 0) {
            return null;
        }

        return (
            <div className="mt-3">
                <div className="text-muted small mb-1">
                    Recent Activity
                </div>

                {activity.map((event, index) => {
                    const line = this.activityLine(event);
                    return (
                        <div
                            key={event.uuid || event.dedupeKey || `activity_${index}`}
                            className="text-truncate"
                            title={line}
                            style={{
                                fontWeight: 600,
                                fontSize: ".85rem"
                            }}
                        >
                            {line}
                        </div>
                    );
                })}
            </div>
        );
    };

    renderPropertiesModal = () => {
        const item = this.state.modal.properties.item;
        if (!item) {
            return null;
        }

        return (
            <Modal
                show={this.state.modal.properties.show}
                title="Properties"
                size="lg"
                buttons={[
                    {
                        caption: "Close",
                        onClick: this.onPropertiesCancel,
                        variant: "secondary",
                        display: true,
                        enabled: true
                    }
                ]}
            >
                <Row className="g-3">
                    <Col md="6">
                        <div className="text-muted small">Name</div>
                        <div className="fw-semibold text-truncate" title={item.name || ""}>
                            {item.name || "Untitled"}
                        </div>
                    </Col>

                    <Col md="3">
                        <div className="text-muted small">Size</div>
                        <div className="fw-semibold">{this.formatBytes(item.size)}</div>
                    </Col>

                    <Col md="3">
                        <div className="text-muted small">Date</div>
                        <div className="fw-semibold">{this.formatDisplayDate(item.updatedAt || item.createdAt) || "â€”"}</div>
                    </Col>

                    <Col xs="12">
                        <div className="top-label">
                            <textarea
                                className="form-control"
                                readOnly
                                rows={5}
                                value={this.filePropertiesDetails(item)}
                                style={{
                                    overflowY: "auto",
                                    resize: "vertical"
                                }}
                            />
                            <label>DETAILS</label>
                        </div>
                    </Col>
                </Row>
            </Modal>
        );
    };

    renderSelectedSummary = () => {
        const summary = this.getSelectedSummary();
        const selectedFile = this.selectedPropertyFile;

        return (
            <Card className="mt-3">
                <Card.Body>
                    <div className="mb-2">
                        <div className="text-muted small">
                            Selected
                        </div>
                        <div style={{ fontWeight: 600 }}>
                            {summary.count} {summary.count === 1 ? "item" : "items"}
                        </div>
                    </div>

                    <div className="mb-2">
                        <div className="text-muted small">
                            Size
                        </div>
                        <div style={{ fontWeight: 600 }}>
                            {this.formatBytes(summary.size)}
                        </div>
                    </div>

                    <div className="mb-2">
                        <div className="text-muted small">
                            Name
                        </div>
                        <div
                            className="text-truncate"
                            title={summary.name}
                            style={{ fontWeight: 600 }}
                        >
                            {summary.name}
                        </div>
                    </div>

                    {summary.date ? <div>
                        <div className="text-muted small">
                            Date
                        </div>
                        <div style={{ fontWeight: 600 }}>
                            {summary.date}
                        </div>
                    </div> : null}
                    {this.renderRecentActivity(selectedFile)}

                    {selectedFile ? (
                        <Button
                            type="button"
                            size="sm"
                            variant="outline-primary"
                            className="mt-3"
                            onClick={this.onPropertiesSelected}
                        >
                            Properties
                        </Button>
                    ) : null}
                </Card.Body>
            </Card>
        );
    };

    renderContextMenu = () => {
        if (!this.state.contextMenu.show || this.state.selected.length === 0) {
            return null;
        }

        const actions = this.selectedActions || [];

        return (
            <div
                className="file-context-menu position-fixed"
                style={{
                    top: this.state.contextMenu.y,
                    left: this.state.contextMenu.x
                }}
                onClick={(event) => event.stopPropagation()}
                onContextMenu={(event) => event.preventDefault()}
            >
                <div className="file-context-menu-caption px-3 py-1 text-muted">
                    {this.state.selected.length} selected
                </div>

                {actions.map((action, index) => (
                    <button
                        key={`file_context_action_${index}`}
                        type="button"
                        className="dropdown-item file-context-menu-action"
                        onClick={() => this.onContextMenuAction(action)}
                    >
                        {action.caption}
                    </button>
                ))}
            </div>
        );
    };

    render() {
        const canGoUp = !!this.state.directoryUuid || !!this.state.virtualPath;
        const isVirtualAgreementPath = ["agreements", "drafts", "finalized"].includes(this.state.virtualPath);
        const showFileManagement = !this.state.virtualPath || (this.isVirtualMatter && !isVirtualAgreementPath);
        const virtualDownloadUnavailable = this.selectedItems.some((item) => item.virtualKind)
            || ["agreements", "drafts"].includes(this.state.virtualPath)
            || (this.state.virtualPath === "finalized" && !this.state.directoryUuid && this.state.selected.length === 0);

        return (
            <>
                <Row>
                    <Col xs="12" xl="4">
                        {showFileManagement ? (
                            <Dropzone
                                label="Upload files"
                                autoRemove
                                maxSizeBytes={1024 * 1024 * 1024}
                                url={this.buildUploadUrl()}
                                onFileUpload={this.onFileUpload}
                                onFilesUpload={this.onFilesUpload}
                            />
                        ) : (
                            <Alert variant="light" className="border">
                                {this.state.virtualPath === "drafts"
                                    ? "Draft agreements open in the Agreement Editor."
                                    : this.state.virtualPath === "finalized"
                                        ? "Finalized agreements are managed as standard matter files."
                                        : "Agreement drafts and finalized documents are organized separately."}
                            </Alert>
                        )}

                        {this.renderSelectedSummary()}
                    </Col>

                    <Col xs="12" xl="8">
                        <Row className="align-items-center mb-3">
                            <Col xs="12" md="6">
                                <h2 className="file-browser-title mt-1">
                                    {this.renderBreadcrumbs()}
                                </h2>
                            </Col>

                            <Col xs="12" md="6" className="text-start text-md-end">
                                <div className="file-browser-toolbar">
                                    {canGoUp ? (
                                        <FileBrowserToolbarButton
                                            onClick={this.onPathUp}
                                            tooltip="Go up one folder"
                                        >
                                            <CsLineIcons icon="arrow-top-left" />
                                        </FileBrowserToolbarButton>
                                    ) : null}

                                    <FileBrowserToolbarButton
                                        disabled={this.state.loading || virtualDownloadUnavailable}
                                        onClick={this.onDownloadSelected}
                                        tooltip={this.state.selected.length > 0 ? "Download selected files and folders" : "Download folder contents"}
                                    >
                                        <CsLineIcons icon="download" />
                                    </FileBrowserToolbarButton>

                                    {showFileManagement ? (
                                        <FileBrowserToolbarButton
                                            onClick={this.onFolderCreate}
                                            tooltip="Create folder"
                                        >
                                            <CreateFolderIcon />
                                        </FileBrowserToolbarButton>
                                    ) : null}
                                </div>
                            </Col>
                        </Row>

                        {this.state.error ? (
                            <Alert variant="danger">
                                {this.state.error}
                            </Alert>
                        ) : null}

                        {this.state.loading ? (
                            <div className="text-muted">
                                Loading files...
                            </div>
                        ) : (
                            <>
                                <Selecto
                                    selectableTargets={[".file-selectable"]}
                                    hitRate={0}
                                    selectByClick={false}
                                    selectFromInside={false}
                                    toggleContinueSelect={["ctrl", "meta"]}
                                    onSelectEnd={this.onDragSelect}
                                />

                                <Row>
                                    <Items
                                        selected={this.state.selected}
                                        items={this.state.items}
                                        onOpen={this.onOpenItem}
                                        onSelect={this.onSelectItem}
                                        onContextMenu={this.onItemContextMenu}
                                    />

                                    {this.state.items.length === 0 ? (
                                        <Col xs="12">
                                            <div className="text-muted">
                                                This folder is empty.
                                            </div>
                                        </Col>
                                    ) : null}
                                </Row>
                            </>
                        )}
                    </Col>
                </Row>

                {this.renderFolderModal()}
                {this.renderRenameModal()}
                {this.renderMoveModal()}
                {this.renderFaxModal()}
                {this.renderPropertiesModal()}
                <Modal
                    show={this.state.modal.destroy.show}
                    title={this.state.modal.destroy.title}
                    body={this.state.modal.destroy.body}
                    buttons={[
                        {
                            default: true,
                            caption: "Cancel",
                            onClick: this.onDestroyCancel,
                            variant: "secondary",
                            display: true,
                            enabled: true
                        },
                        {
                            caption: "Delete",
                            onClick: this.onDestroyOk,
                            variant: "danger",
                            display: true,
                            enabled: true
                        }
                    ]}
                />

                <Modal
                    show={this.state.modal.error.show}
                    title={this.state.modal.error.title}
                    body={this.state.modal.error.body}
                    buttons={[
                        {
                            caption: "OK",
                            onClick: this.onErrorOk,
                            variant: "outline-primary"
                        }
                    ]}
                />

                <Shares
                    show={this.state.modal.share.show}
                    onCancel={this.onShareCancel}
                    onOk={this.onShareOk}
                    eligible={this.state.eligible}
                    shares={this.state.modal.share.shares}
                    options={{ role: this.selectedShareRole }}
                    onSearch={this.loadShareEligible}
                />

                {this.renderContextMenu()}
            </>
        );
    }
}

Files.propTypes = {
    ownerType: PropTypes.string.isRequired,
    ownerUuid: PropTypes.string.isRequired,
    contextType: PropTypes.string,
    contextUuid: PropTypes.string,
    getMatters: PropTypes.func,
    loadAgreements: PropTypes.func,
    onOpenAgreement: PropTypes.func,

    listDirectories: PropTypes.func.isRequired,
    listFiles: PropTypes.func.isRequired,
    createDirectory: PropTypes.func.isRequired,
    downloadFile: PropTypes.func.isRequired,
    downloadContents: PropTypes.func.isRequired,
    downloadMany: PropTypes.func.isRequired,
    deleteFile: PropTypes.func.isRequired,
    deleteDirectory: PropTypes.func.isRequired,

    eligibleShares: PropTypes.func.isRequired,
    listShares: PropTypes.func.isRequired,
    applyShares: PropTypes.func.isRequired,

    renameFile: PropTypes.func.isRequired,
    renameDirectory: PropTypes.func.isRequired,

    moveFile: PropTypes.func.isRequired,
    moveDirectory: PropTypes.func.isRequired,
    createSignatureEnvelope: PropTypes.func.isRequired,
    sendFax: PropTypes.func.isRequired,
    searchPeople: PropTypes.func.isRequired,
};

Files.defaultProps = {
    contextType: null,
    contextUuid: null
};

const mapStateToProps = (state) => ({
    auth: state.auth
});

export default connect(mapStateToProps, {
    listFiles,
    listDirectories,
    createDirectory,
    downloadFile,
    downloadContents,
    downloadMany,
    deleteFile,
    deleteDirectory,
    eligibleShares,
    listShares,
    applyShares,
    renameFile,
    renameDirectory,
    moveFile,
    moveDirectory,
    getMatters,
    createSignatureEnvelope,
    sendFax,
    searchPeople
})(Files);
